'use client';

import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';
import { Button } from '@ui/components/ui/button';
import { Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useAnnouncements } from '@/hooks/useAnnouncements';
import Link from 'next/link';

export function AnnouncementsDialog() {
  const { announcements, markAsViewed } = useAnnouncements();

  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const t = useTranslations('FeatureAnnouncements');
  const tCommon = useTranslations('Common');

  const currentAnnouncement = announcements[currentIndex];
  const hasMultiple = announcements.length > 1;

  const handleClose = async () => {
    setIsClosing(true);
    try {
      await markAsViewed();
      setOpen(false);
    } catch (error) {
      console.error('Failed to mark announcements as viewed:', error);
    } finally {
      setIsClosing(false);
    }
  };

  const handleNext = () => {
    if (currentIndex < announcements.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  useEffect(() => {
    if (announcements.length > 0) {
      setOpen(true);
    }
  }, [announcements]);

  if (!currentAnnouncement) return null;
  const isLastAnnouncement = currentIndex === announcements.length - 1;

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <DialogTitle>{t('title')}</DialogTitle>
          </div>
          {hasMultiple && (
            <DialogDescription>
              {t('announcement')} {currentIndex + 1} {t('of')} {announcements.length}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4">
          {currentAnnouncement.imageUrl && (
            <div className="relative w-full h-[40vh] rounded-lg overflow-hidden bg-muted">
              <Image src={currentAnnouncement.imageUrl} alt={currentAnnouncement.title} fill className="object-cover" />
            </div>
          )}

          <div>
            <h3 className="text-lg font-semibold mb-2">{currentAnnouncement.title}</h3>
            <div className="text-sm text-muted-foreground whitespace-pre-wrap">{currentAnnouncement.description}</div>
          </div>

          {currentAnnouncement.ctaUrl && (
            <div className="pt-2">
              <Button asChild variant="default">
                <Link href={currentAnnouncement.ctaUrl} onClick={isLastAnnouncement ? handleClose : handleNext}>
                  {t('startTutorial')}
                </Link>
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="items-center sm:justify-between">
          {hasMultiple ? (
            <Button size="sm" variant="outline" onClick={handleClose} disabled={isClosing}>
              {tCommon('actions.skipAll')}
            </Button>
          ) : (
            <div />
          )}
          <div className="flex gap-2 items-center">
            {hasMultiple ? (
              <>
                <Button variant="outline" size="sm" onClick={handlePrevious} disabled={currentIndex === 0}>
                  <ChevronLeft className="h-4 w-4" />
                  {tCommon('actions.previous')}
                </Button>
                <Button onClick={isLastAnnouncement ? handleClose : handleNext}>
                  {isLastAnnouncement ? tCommon('actions.gotIt') : tCommon('actions.next')}
                  {!isLastAnnouncement && <ChevronRight className="h-4 w-4" />}
                </Button>
              </>
            ) : (
              <Button onClick={handleClose}>{tCommon('actions.gotIt')}</Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
